import React from 'react';
import { previewSyncNaver } from '../../api/api';
import type { SyncNaverPreview } from '../../types/syncPreview';
import { FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, RADIUS, SPACING } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { appModal } from '../../utils/appModal';
import { appMessage as message } from '../../utils/appMessage';
import { formatPrice } from '../../utils/format';
import { blockedParts, formatDuration, formatRate } from '../../utils/syncPreview';
import { blockedText, currentStoreHeader } from '../../http';

/** 확정 대기 목록 잘림의 사유(백엔드 PR #82 — 시작 단계 409 의 뜻과 같게). */
export const TRUNCATED_REASON =
  'PriceHub 확정 대기 목록이 1,000건을 넘어 잘렸습니다. PriceHub 대시보드에서 대기를 확정 · 무시해 줄인 뒤 다시 반영해 주세요.';

/**
 * ③ 스토어 반영 확인 창(감사 card-controltower#538 · Q7 #531, 백엔드 PR #76) — 누르면 먼저 요약(보낼 수 · 상승/하락 · 상승 상한 초과 ·
 * 막힘 세 갈래 · 큰 변동 5건 · 예상 소요)을 보이고, 한 번 더 눌러야 실제 반영(/sync-naver)이 나간다. 네이버 판매가를 바꾸는 쓰기라
 * 몇 건이 어느 쪽으로 얼마나 바뀌는지 보고 누르게. 요약을 못 받으면(옛 서버 · 오류) 건수만 묻는다 — 반영을 막지는 않는다.
 */
export const SyncPreviewSummary: React.FC<{
  preview: SyncNaverPreview | null;
  /** 보내려는 카드 수(요약을 못 받았을 때 쓴다). */
  requested: number;
  /** 화면에서 고른 일부만 보내는가. */
  partial?: boolean;
}> = ({ preview, requested, partial }) => {
  const t = useThemeTokens();
  const line: React.CSSProperties = { fontSize: FONT_SIZE.sm, color: t.textPrimary, lineHeight: LINE_HEIGHT.normal };
  const box = (color: string, bg: string, border: string): React.CSSProperties => ({
    fontSize: FONT_SIZE.sm, color, background: bg, border: `1px solid ${border}`, borderRadius: RADIUS.md,
    padding: `${SPACING.xs}px ${SPACING.sm}px`, lineHeight: LINE_HEIGHT.normal,
  });

  if (!preview) {
    return (
      <div data-testid="sync-preview" style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs }}>
        <div style={box(t.textPrimary, t.tintWarning, t.lineWarning)}>
          반영 요약을 불러오지 못했습니다 — {partial ? '고른 ' : ''}{requested.toLocaleString()}건을 그대로 네이버 판매가로 반영합니다.
          확정 대기 · 요청 대기 카드는 반영 때 건너뜁니다.
        </div>
      </div>
    );
  }

  const parts = blockedParts(preview);
  const capPct = Math.round(preview.riseCapRate * 100);
  return (
    <div data-testid="sync-preview" style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs }}>
      {/* 확정 대기 목록이 잘리면 ③ · 롤백은 409 로 막힌다(백엔드 PR #82 fail-closed, 감사 #700) — 예전(#576)의 '경고만' 이 아니다.
          실행 버튼도 막고(confirmSyncNaver), 첫 줄도 "N건을 반영합니다" 대신 막힘 사유로(시우 검수 — 두 줄이 엇갈렸다). */}
      {preview.hubPendingTruncated ? (
        <div data-testid="sync-preview-truncated" role="alert" style={box(t.textPrimary, t.tintDanger, t.lineDanger)}>
          {blockedText(TRUNCATED_REASON, '반영할 수 없습니다')}
        </div>
      ) : (
        <div style={line}>
          {partial ? '고른 카드 중 ' : ''}<b data-testid="sync-preview-send">{preview.sendCount.toLocaleString()}건</b>을 네이버 판매가로 반영합니다
          {' — '}상승 {preview.riseCount.toLocaleString()} · 하락 {preview.dropCount.toLocaleString()}
          {preview.noBaseCount > 0 ? ` · 네이버 가격 없음 ${preview.noBaseCount.toLocaleString()}` : ''}
          {preview.sendCount > 0 ? ` · 예상 ${formatDuration(preview.estimatedSeconds)}` : ''}
        </div>
      )}
      {preview.sendCount === 0 && (
        <div style={box(t.textPrimary, t.bgRaised, t.border)}>보낼 카드가 없습니다 — 모두 네이버 가격과 같거나 확정 대기입니다.</div>
      )}
      {preview.overRiseCapCount > 0 && (
        <div data-testid="sync-preview-cap" style={box(t.textPrimary, t.tintWarning, t.lineWarning)}>
          <b style={{ color: t.warning }}>상승률 {capPct}% 초과 {preview.overRiseCapCount.toLocaleString()}건</b>
          {' '}— 자동 반영이라면 막히는 크기입니다. 아래 큰 변동을 확인하세요(수동 반영은 막지 않습니다).
        </div>
      )}
      {preview.blockedCount > 0 && (
        <div data-testid="sync-preview-blocked" style={box(t.textPrimary, t.bgRaised, t.border)}>
          <b>건너뜀 {preview.blockedCount.toLocaleString()}건</b>{parts.length > 0 ? ` — ${parts.join(' · ')}` : ''}
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
            실패가 아닙니다 — PriceHub 관리자가 확정 · 무시한 뒤 다음 반영에 들어갑니다.
            {!preview.hubPendingChecked && ' PriceHub 확정 대기 목록을 읽지 못해 CT 표시만으로 걸렀습니다.'}
          </div>
        </div>
      )}
      {preview.notNeeded > 0 && (
        <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
          반영할 것 없음 {preview.notNeeded.toLocaleString()}건(이미 네이버 가격과 같음 등)은 보내지 않습니다.
        </div>
      )}
      {preview.topChanges.length > 0 && (
        <div>
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, marginBottom: SPACING.xxs }}>변동이 큰 {preview.topChanges.length}건</div>
          <ul data-testid="sync-preview-top" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {preview.topChanges.map((c) => (
              <li key={c.id} style={{
                display: 'flex', alignItems: 'baseline', gap: SPACING.xs, fontSize: FONT_SIZE.xs,
                borderBottom: `1px solid ${t.borderSubtle}`, padding: `2px 0`,
              }}>
                <span title={`${c.productName}${c.sellerProductCode ? ` · ${c.sellerProductCode}` : ''}`} style={{
                  flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: t.textPrimary,
                }}>
                  {c.productName || c.sellerProductCode || `#${c.id}`}
                </span>
                <span style={{ whiteSpace: 'nowrap', color: t.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                  {formatPrice(c.currentPrice)} → <b style={{ color: t.textPrimary }}>{formatPrice(c.suggestedPrice)}</b>
                </span>
                <span style={{
                  whiteSpace: 'nowrap', minWidth: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                  fontWeight: FONT_WEIGHT.semibold, color: c.rate > 0 ? t.accent : t.danger,
                }}>
                  {formatRate(c.rate)}
                </span>
                {c.overRiseCap && <span style={{ color: t.warning, fontWeight: FONT_WEIGHT.semibold, whiteSpace: 'nowrap' }}>상한 초과</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/** 요약 대기 한도 — 응답이 오지 않으면 요약 없이 묻는다(창이 영영 안 뜨지 않게, 감사 #827 과 같은 방식). */
export const SYNC_PREVIEW_TIMEOUT_MS = 10_000;

const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> => new Promise<T>((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('timeout')), ms);
  p.then((v) => { clearTimeout(timer); resolve(v); }, (e) => { clearTimeout(timer); reject(e); });
});

export interface ConfirmSyncOptions {
  /** 화면에서 고른 일부만 보내는가. */
  partial?: boolean;
  /**
   * 부른 쪽(useCardJobs)의 생명주기 — 화면이 내려가면(로그아웃 · 세션 만료 · 매장 전환 재마운트) abort 해 떠 있는 창을 닫고 false
   * (감사 #839 — 예전 모듈 전역 플래그는 창이 안 풀리면 그 탭의 ③ 을 전부 막았다). 연타 방지도 부른 쪽 ref 가 한다.
   */
  signal?: AbortSignal;
  /**
   * 창을 연 시점의 매장(http.ts currentStoreHeader) — 요약을 이 매장으로 받고(storeOverride, 감사 #828), 받는 사이 매장이 바뀌었으면
   * 창을 열지 않고 false. 주지 않으면 보내는 순간의 매장.
   */
  store?: string | null;
}

/**
 * ③ 확인 — 요약을 받아 확인 창을 띄우고, '반영' 을 누르면 true. 취소 · 창이 닫힘 · 부른 쪽 abort · 매장이 바뀜이면 false.
 * 요약 API 가 실패해도(옛 서버 404 · 오류 · 시간 초과) 요약 없이 건수만 묻는다.
 */
export async function confirmSyncNaver(ids: number[], opts: ConfirmSyncOptions = {}): Promise<boolean> {
  const { signal } = opts;
  if (signal?.aborted) return false;
  const pinned = 'store' in opts;
  let preview: SyncNaverPreview | null = null;
  message.loading({ key: 'sync-preview', content: '반영할 내용을 확인하는 중…', duration: 0 });
  try {
    preview = await withTimeout(
      pinned ? previewSyncNaver(ids, { store: opts.store ?? null }) : previewSyncNaver(ids),
      SYNC_PREVIEW_TIMEOUT_MS,
    );
  } catch {
    preview = null;
  }
  if (signal?.aborted) {
    message.open({ key: 'sync-preview', type: 'info', content: '화면이 바뀌어 확인을 멈췄습니다', duration: 2 });
    return false;
  }
  if (pinned && currentStoreHeader() !== (opts.store ?? null)) {
    message.open({ key: 'sync-preview', type: 'warning', content: '매장이 바뀌어 반영하지 않았습니다 — 다시 확인해 주세요', duration: 8 });
    return false;
  }
  message.open({ key: 'sync-preview', type: 'info', content: '반영 전 확인 창을 확인하세요', duration: 1 });
  // 확정 대기 잘림이면 서버가 409 로 거절한다(백엔드 PR #82) — 실행 버튼을 막고 사유만 보인다(감사 #700).
  const blocked = preview?.hubPendingTruncated === true;
  return new Promise<boolean>((resolve) => {
    let done = false;
    let modal: { destroy?: () => void } | undefined;
    const onAbort = () => { modal?.destroy?.(); finish(false); };
    function finish(v: boolean) {
      if (done) return;
      done = true;
      signal?.removeEventListener('abort', onAbort);
      resolve(v);
    }
    modal = appModal.confirm({
      title: '③ 스토어 반영 — 네이버 판매가를 바꿉니다',
      icon: null,
      width: 560,
      content: <SyncPreviewSummary preview={preview} requested={ids.length} partial={opts.partial} />,
      okButtonProps: { disabled: blocked },
      okText: blocked ? '반영 막힘'
        : preview ? `${preview.sendCount.toLocaleString()}건 반영` : `${ids.length.toLocaleString()}건 반영`,
      cancelText: '취소',
      onOk: () => finish(true),
      onCancel: () => finish(false),
      // 누르지 않고 닫혀도(destroy · 다른 길) 끝낸다 — 약속이 안 풀린 채 남지 않게.
      afterClose: () => finish(false),
    }) as { destroy?: () => void } | undefined;
    signal?.addEventListener('abort', onAbort, { once: true });
    if (signal?.aborted) onAbort();
  });
}